import DOMPurify from 'dompurify';

/**
 * HTML Sanitization Utilities
 *
 * These utilities provide safe HTML handling for rich text content,
 * preventing XSS attacks while allowing basic text formatting.
 */

// Allowed HTML tags for rich text (bold, italic, underline, strikethrough, colors)
const ALLOWED_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'span'];

/**
 * Sanitizes HTML content to prevent XSS attacks
 * Removes all dangerous tags, attributes, and styles
 *
 * @param html - Raw HTML string to sanitize
 * @returns Sanitized HTML safe for rendering
 */
export const sanitizeHtml = (html: string): string => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ['style'], // Allow style attribute on all allowed tags
    KEEP_CONTENT: true, // Keep text content even if tags are removed
  });
};

/**
 * Normalizes HTML content by removing empty paragraphs
 * Converts empty editor states to empty strings
 *
 * @param html - HTML string to normalize
 * @returns Normalized HTML or empty string
 */
export const normalizeHtml = (html: string): string => {
  const trimmed = html.trim();

  // Empty paragraph variations from TipTap editor
  if (trimmed === '<p></p>' || trimmed === '<p><br></p>' || trimmed === '') {
    return '';
  }

  return trimmed;
};

/**
 * Checks if content is plain text (no HTML tags)
 *
 * @param content - String to check
 * @returns True if plain text, false if contains HTML
 */
export const isPlainText = (content: string): boolean => {
  return !/<[a-z][\s\S]*>/i.test(content);
};

/**
 * Wraps plain text in paragraph tags for editor compatibility
 * Leaves existing HTML unchanged
 *
 * @param text - Plain text or HTML string
 * @returns HTML-wrapped content
 */
export const wrapPlainText = (text: string): string => {
  if (!text) return '';

  // If already HTML, return as-is
  if (!isPlainText(text)) {
    return text;
  }

  // Wrap plain text in paragraph tag
  return `<p>${text}</p>`;
};
